/**
 * The team's own super skills (docs/refactor/51, §6.2): a plugin's commands placed in "superskills" join the
 * Super skills grid, with their menu of options, for everybody in the organization — no install, the team made
 * them for the team. One way to have a button that sends a prompt, not two: Plugins and Super skills are the same
 * thing seen from the developer's side and from everybody else's.
 *
 * A team skill's prompt is its own (the manifest's), not a catalog key; `promptFor` answers both.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import type { PluginItem, SuperSkill } from './types'

const PREFIX = 'team:'

/** Plugin icons to the grid's own (anything else falls back to the grid's default). */
const ICON: Record<string, string> = { book: 'file-text', sparkles: 'search', gauge: 'sheet', rocket: 'globe', bug: 'file-search' }

/** Every super skill the team published, as the grid shows them, and each one's prompt by its key. */
export function teamSuperSkills(plugins: PluginItem[]): { skills: SuperSkill[]; prompts: Record<string, string> } {
  const skills: SuperSkill[] = []
  const prompts: Record<string, string> = {}
  for (const plugin of plugins) {
    for (const command of plugin.commands.filter((c) => c.placement === 'superskills')) {
      const key = `${PREFIX}${plugin.id}:${command.id}`
      const fields = command.fields ?? []
      // The team's own words, whatever the interface language: one text serves both.
      const same = (text: string) => ({ 'pt-BR': text, 'en-US': text })
      prompts[key] = command.prompt
      skills.push({
        key,
        icon: ICON[command.icon] ?? command.icon,
        title: same(command.title),
        description: same(command.description || plugin.description),
        prompt: key,
        shown: same(fields.length > 0 ? `${command.title}: {{${fields[0]!.key}}}` : command.title),
        fields: fields.map((f) => ({
          key: f.key,
          type: f.type,
          label: same(f.label),
          hint: f.hint ? same(f.hint) : null,
          options: f.options?.map((o) => ({ value: o.value, label: same(o.label) })) ?? null,
          default: f.default ?? null,
          required: f.required ?? false,
        })),
        team: plugin.name,
      })
    }
  }
  return { skills, prompts }
}

export function isTeamSkill(key: string): boolean {
  return key.startsWith(PREFIX)
}

/** The catalog's super skills and the team's, and the prompt for any of them. */
export function useSuperSkills(enabled = true) {
  const { data: recommended } = useQuery({ queryKey: ['recommended'], queryFn: api.recommended, staleTime: 10 * 60_000, enabled })
  const { data: plugins } = useQuery({ queryKey: ['plugins'], queryFn: api.plugins.list, staleTime: 60_000, retry: false, enabled })
  return useMemo(() => {
    const team = teamSuperSkills(plugins ?? [])
    return {
      skills: enabled ? [...(recommended?.superSkills ?? []), ...team.skills] : [],
      teamPrompt: (key: string): string | null => team.prompts[key] ?? null,
    }
  }, [enabled, recommended, plugins])
}
